import React, { useEffect, useRef, useState } from 'react';
import { Tooltip, Typography } from '@mui/material';
import type { SxProps, Theme, TooltipProps, TypographyProps } from '@mui/material';

interface ClampedTextTooltipProps {
  text: string;
  lineClamp?: number;
  variant?: TypographyProps['variant'];
  sx?: SxProps<Theme>;
  tooltipPlacement?: TooltipProps['placement'];
}

const OVERFLOW_EPSILON = 1;

const isOverflowing = (element: HTMLElement) =>
  element.scrollHeight > element.clientHeight + OVERFLOW_EPSILON ||
  element.scrollWidth > element.clientWidth + OVERFLOW_EPSILON;

const ClampedTextTooltip: React.FC<ClampedTextTooltipProps> = ({
  text,
  lineClamp = 2,
  variant = 'body2',
  sx,
  tooltipPlacement = 'right-start',
}) => {
  const textRef = useRef<HTMLSpanElement | null>(null);
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    const element = textRef.current;
    if (!element) return;

    const updateOverflow = () => {
      setShowTooltip(isOverflowing(element));
    };

    updateOverflow();
    const frame = requestAnimationFrame(updateOverflow);
    const resizeObserver =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateOverflow) : null;

    resizeObserver?.observe(element);

    return () => {
      cancelAnimationFrame(frame);
      resizeObserver?.disconnect();
    };
  }, [lineClamp, text]);

  return (
    <Tooltip
      title={showTooltip ? text : ''}
      placement={tooltipPlacement}
      enterDelay={700}
      enterNextDelay={700}
      disableInteractive
      disableHoverListener={!showTooltip}
    >
      <Typography
        ref={textRef}
        component="span"
        variant={variant}
        onMouseEnter={(event) => {
          setShowTooltip(isOverflowing(event.currentTarget));
        }}
        sx={[
          {
            minWidth: 0,
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: lineClamp,
            overflow: 'hidden',
            whiteSpace: 'normal',
            wordBreak: 'break-word',
          },
          ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
        ]}
      >
        {text}
      </Typography>
    </Tooltip>
  );
};

export default ClampedTextTooltip;
